type QueueHandler<
  T extends {
    id?: string;
  },
> = (items: T[]) => void | Promise<void>;

export class DebouncedQueue<
  T extends {
    id?: string;
  },
> {
  private queue: T[] = [];
  // PSTUDIO-3611: position index for keyed items. The old dedupe ran
  // queue.findIndex per added item — O(M²) when a paste enqueued ~5500 order
  // tuples in one call. The index keeps add O(1) while preserving the exact
  // replace-in-place ordering semantics.
  private indexById = new Map<string, number>();
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private handler: QueueHandler<T>,
    private delay: number
  ) {}

  /**
   * Adds items to the queue (an existing id is replaced in place) and resets
   * the debounce timer.
   */
  public add(items: T[]): void {
    for (const item of items) {
      if (!item.id) {
        this.queue.push(item);
        continue;
      }
      const existing = this.indexById.get(item.id);
      if (existing !== undefined) {
        this.queue[existing] = item;
      } else {
        this.indexById.set(item.id, this.queue.length);
        this.queue.push(item);
      }
    }

    this.resetTimer();
  }

  /**
   * Resets the debounce timer.
   */
  private resetTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.delay);
  }

  /**
   * Processes all items in the queue immediately and clears the queue.
   */
  public flush(): void {
    if (this.queue.length > 0) {
      this.handler(this.queue);
      this.queue = [];
      this.indexById.clear();
      this.cancel(); // Clear the timer after flushing
    }
  }

  /**
   * Drops any queued items whose `id` is in `ids` without flushing them.
   * Used when a row is hard-deleted (e.g. an emptied group dissolving): a
   * still-queued UPSERT for that row would race the immediate delete and
   * resurrect it in Postgres on the next flush. Returns the number of
   * entries removed.
   */
  public remove(ids: string[]): number {
    if (this.queue.length === 0 || ids.length === 0) return 0;
    const drop = new Set(ids);
    const before = this.queue.length;
    this.queue = this.queue.filter((item) => !(item.id && drop.has(item.id)));
    // Positions shifted — rebuild the index in one pass.
    this.indexById.clear();
    this.queue.forEach((item, i) => {
      if (item.id) this.indexById.set(item.id, i);
    });
    return before - this.queue.length;
  }

  /**
   * Number of items waiting to flush. Cheap synchronous indicator used by
   * cross-tab sync guards (PSTUDIO-3610: never reload over pending saves).
   */
  public get size(): number {
    return this.queue.length;
  }

  /**
   * The `id`s of all keyed items still waiting to flush. PSTUDIO-3929 (a): the
   * per-row clobber guard in mergeVersionData needs to know WHICH rows have a
   * queued-but-not-yet-persisted local write, so an incoming coworker merge
   * skips exactly those and never overwrites a just-pasted cell. Items pushed
   * without an `id` are not tracked (rows always carry one).
   */
  public pendingIds(): Set<string> {
    return new Set(this.indexById.keys());
  }

  /**
   * Cancels the pending flush operation and clears the queue.
   */
  public cancel(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.queue = [];
    this.indexById.clear();
  }
}
